import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState, useRecoilValueLoadable, useResetRecoilState } from 'recoil';
import { VehicleService, EnumGetObeDetailsResultDepositStatus } from 'api';
import { getActiveAgreementByIdRequestIdState } from 'api/agreements/getActiveAgreementsForCustomer';
import { getAgreementsRequestIdState } from 'api/agreements/getAgreementsAndVehicle';
import { isObeBeingMoved as isObeBeingMovedSelector, obeBeingMovedRequestId } from 'api/obes/getIsObeBeingMoved';
import { getObeDetailsRequestIdState, getObeDetails as getObeDetailsSelector } from 'api/obes/getObeDetails';
import { getObesForCustomerRequestIdState } from 'api/obes/getObesForCustomer';
import { isVehicleAlreadyRegisteredRequestId as isVehicleAlreadyRegisteredRequestIdAtom } from 'api/vehicles/isVehicleAlreadyRegistered';
import { isVehicleAwaitingShippingRequestId as isVehicleAwaitingShippingRequestIdAtom } from 'api/vehicles/isVehicleAwaitingShipping';
import { RECENTLY_REPLACED } from 'constants/text';
import useCurrentCustomer from 'hooks/customer/useCurrentCustomer';
import deliveryAddressAtom from 'state/address';
import { chosenAgreement as chosenAgreementAtom, newAgreementName as newAgreementNameAtom, agreementOption as agreementOptionAtom } from 'state/agreement';
import { transferDepositAtom, transferTag as transferTagState, transferVehicle, vehicleInsuranceRegistration, changeVehicleAtom } from 'state/transferObe';
import { mapIVehicleToRegisterVehicle } from 'utils/mapping/mapVehicles';
import checkLoadable from 'utils/recoil/checkLoadable';
const useTransferTag = () => {
  const {
    t
  } = useTranslation();
  const {
    customer
  } = useCurrentCustomer();
  const {
    enqueueSnackbar
  } = useSnackbar();

  // States
  const [loading, setLoading] = useState(false);
  const [tag, setTag] = useRecoilState(transferTagState);
  const [vehicle, setVehicle] = useRecoilState(transferVehicle);
  const [shouldTransferDeposit, setShouldTransferDeposit] = useRecoilState(transferDepositAtom);
  const setRegistartionNumberForInsurance = useSetRecoilState(vehicleInsuranceRegistration);
  const [changeVehicle, setChangeVehicle] = useRecoilState(changeVehicleAtom);
  const isObeBeingMovedLoadable = useRecoilValueLoadable(isObeBeingMovedSelector(tag?.obeId));
  const {
    contents: isObeBeingMoved
  } = checkLoadable(isObeBeingMovedLoadable);
  const setObeDetailsRequestId = useSetRecoilState(getObeDetailsRequestIdState);
  const setAgreementsRequestId = useSetRecoilState(getAgreementsRequestIdState);
  const setObesForCustomerRequestId = useSetRecoilState(getObesForCustomerRequestIdState);
  const setActiveAgreementRequestId = useSetRecoilState(getActiveAgreementByIdRequestIdState);
  const setIsVehicleAlreadyRegisteredRequestId = useSetRecoilState(isVehicleAlreadyRegisteredRequestIdAtom);
  const setIsVehicleAwaitingShippingRequestId = useSetRecoilState(isVehicleAwaitingShippingRequestIdAtom);
  const setObeBeingMovedRequestId = useSetRecoilState(obeBeingMovedRequestId);
  const chosenAgreement = useRecoilValue(chosenAgreementAtom);
  const newAgreementName = useRecoilValue(newAgreementNameAtom);
  const getFromTagVehicle = useRecoilValueLoadable(getObeDetailsSelector(tag?.obeNumber));
  const {
    contents: fromVehicle
  } = checkLoadable(getFromTagVehicle);

  // Reset functions
  const resetAgreement = useResetRecoilState(chosenAgreementAtom);
  const resetAgreementName = useResetRecoilState(newAgreementNameAtom);
  const resetVehicle = useResetRecoilState(transferVehicle);
  const resetTag = useResetRecoilState(transferTagState);
  const resetTransferDeposit = useResetRecoilState(transferDepositAtom);
  const resetAgreementOption = useResetRecoilState(agreementOptionAtom);
  const resetDeliveryAddress = useResetRecoilState(deliveryAddressAtom);
  const resetChangeVehicle = useResetRecoilState(changeVehicleAtom);
  const setInsuranceRegAndResetVehicle = () => {
    setRegistartionNumberForInsurance(prev => vehicle?.registrationNumber || fromVehicle?.registrationNumber || prev);
    resetVehicle();
  };
  const resetTransferTag = () => {
    setAgreementsRequestId(prev => prev + 1);
    resetAgreement();
    resetAgreementName();
    resetAgreementOption();
    setInsuranceRegAndResetVehicle();
    resetTag();
    resetTransferDeposit();
    resetDeliveryAddress();
    resetChangeVehicle();
    setObeDetailsRequestId(prev => prev + 1);
    setObesForCustomerRequestId(prev => prev + 1);
    setActiveAgreementRequestId(prev => prev + 1);
    setIsVehicleAlreadyRegisteredRequestId(prev => prev + 1);
    setIsVehicleAwaitingShippingRequestId(prev => prev + 1);
    setObeBeingMovedRequestId(prev => prev + 1);
  };
  const registerVehicleAndMoveObe = async vehicleToBeRegistered => {
    const vehicleId = await fromVehicle?.vehicleId;
    const body = {
      ...vehicleToBeRegistered,
      obeId: fromVehicle?.obeId,
      // If changeVehicle = true, then vehicleId should be null
      // vehicleId is the vehicle the tag will be transfered to
      targetVehicleId: changeVehicle ? null : vehicleId,
      customerId: customer?.id,
      targetAgreementId: chosenAgreement?.id,
      agreementName: newAgreementName,
      shouldTransferDeposit
    };
    const res = await VehicleService.registerVehicleAndMoveObe({
      body
    });
    return res;
  };
  const handleError = e => {
    let errorMessage = t('error.transfer_tag');
    if (axios.isAxiosError(e) && e.response?.status === 400) {
      if (e.response.data.includes(RECENTLY_REPLACED)) {
        errorMessage = t('error.recently_replaced');
      } else {
        errorMessage = t('error.obe_awaiting_approval');
      }
    } else if (axios.isAxiosError(e) && e.response?.status === 422 && e.response?.data == 1) {
      errorMessage = t('error.move_obe_same_agreement_and_vehicle');
    } else if (axios.isAxiosError(e) && e.response?.status === 422 && e.response?.data == 2) {
      errorMessage = t('error.obe_is_not_moveable');
    } else if (e instanceof Error) {
      errorMessage = e.message;
    }
    enqueueSnackbar(errorMessage, {
      variant: 'error',
      autoHideDuration: 10000
    });
    console.error(e);
  };
  const transferTag = async () => {
    const mappedVehicle = mapIVehicleToRegisterVehicle(vehicle);
    setRegistartionNumberForInsurance(mappedVehicle?.registrationNumber || fromVehicle?.registrationNumber);
    const vehicleToBeRegistered = {
      ...mappedVehicle,
      currentAgreementId: fromVehicle?.agreementId
    };
    setLoading(true);
    const res = registerVehicleAndMoveObe(vehicleToBeRegistered);
    return res.then(() => {
      resetTransferTag();
      return true;
    }).catch(e => {
      handleError(e);
      return null;
    }).finally(() => {
      setLoading(false);
    });
  };
  const isTransferValid = fromVehicle?.obeId && !loading && (!changeVehicle || vehicle) && (chosenAgreement || newAgreementName) && !isObeBeingMoved;
  const vehicleHasDeposit = [EnumGetObeDetailsResultDepositStatus.Paid, EnumGetObeDetailsResultDepositStatus.PartialReturned, EnumGetObeDetailsResultDepositStatus.Requested].includes(fromVehicle?.depositStatus);
  return {
    tag,
    vehicle,
    setTag,
    loading,
    setVehicle,
    fromVehicle,
    transferTag,
    resetTransferTag,
    shouldTransferDeposit,
    setShouldTransferDeposit,
    changeVehicle,
    setChangeVehicle,
    isTransferValid,
    isObeBeingMoved,
    vehicleHasDeposit
  };
};
export default useTransferTag;